import State from "@/helpers/pattern/state";
import { Campana } from "@/models/operativos/campana";
import { modelIsModified } from "@/helpers/vue-mc";
import campanaAPI from "@/services/operativos/campana";
import CombosAPI from "@/services/catalogos";
import { EventBus } from "@/events/event-bus";
import * as events from "@/constants/event-names";
import { clearSwal } from "@/constants/sweet-alerts";
import { scrollToForm } from "@/helpers/form-scroll-to";
import { getDateFromLocalToUTC } from "@/helpers/date-methods";
import { setSelectsInactiveData } from "@/helpers/form-common";

/**
 * State Pattern for status form
 */
export default class CampanaStates extends State {
  /**
   * General Methods
   */
  base() {
    return {
      clear({ $setState }) {
        $setState("initial");
      }
      , async askForClear({ $data }) {
        try {
          if ((await $data.$swal(clearSwal)).value) this.clear();
        } catch (error) {
          console.error(error);
        }
      }
      , resetModel({ $data }) {
        $data.campana = new Campana();
        $data.refreshkey += 1;
      }
      , resetValidators({ $data }) {
        $data.$v.$reset();
        $data.refreshkey += 1;
      }
      , isDisableButtonSubmit({ $data }) {
        return $data.hasErrors($data.$v.campana) || $data.view.action.submit.busy || !modelIsModified($data.campana);
      }
      , async loadSelectsData({ $data }) {
        const { data: { results: { combo: vendorList } } } = await CombosAPI.get({ option: "vendor" });
        $data.view.elem.select.vendor.filteredList = vendorList ? vendorList.filter((value) => value.status) : [];
        $data.view.elem.select.vendor.completeList = vendorList || [];
      }
    };
  }

  /**
   * States and their methods
   */
  states() {
    return {
      initial: {
        $constructor: function ({ $data }) {
          // Reset Models and validators
          this.resetModel();
          this.loadSelectsData();
          this.resetValidators();

          // View configuration buttons
          $data.view.elem.button.submit.class.current = $data.view.elem.button.submit.class.save;
          $data.view.elem.button.submit.title.current = $data.view.elem.button.submit.title.save;
        }
        , async submit({ $data }, {
          descripcion, descripcionapp, se, vendor, canales, datelimini, datelimend, dateresini, dateresend, cuotasimple, benefpref, contactpref, status, fincampanaapp, oob
        }) {
          try {
            if (!$data.isAllValid($data.$v)) {
              EventBus.$emit(events.SHOW_TOASTER_MESSAGE, "error", "VL0000", $data.$t("errors.validof"));
              scrollToForm($data);
              return false;
            }

            $data.view.action.submit.busy = true;

            if (oob) {
              descripcion = descripcion.toLowerCase().includes("oob") ? descripcion : "OOB_" + descripcion;
            } else {
              descripcion = descripcion.replace("OOB_", "");
            }

            const response = await campanaAPI.save({
              clave: null
              , descripcion
              , descripcionapp
              , se: se ? Number(se) : null
              , vendor: vendor ? Number(vendor) : null
              , canales: canales.join()
              , datelimini
              , datelimend
              , dateresini
              , dateresend
              , cuotasimple
              , benefpref
              , contactpref
              , status
              , fincampanaapp
              , oob
            });

            $data.view.action.submit.busy = false;
            this.resetModel();
            this.resetValidators();
            $data.$refs.campanaTable.refreshData();
            return response;
          } catch (error) {
            console.error(error);
            $data.view.action.submit.busy = false;
            return error;
          }
        }
      }
      , editing: {
        $constructor: function (
          { $data },
          {
            clave, descripcion, descripcionapp, se, vendor, canales, datelimini, datelimend, dateresini, dateresend, cuotasimple, benefpref, contactpref, status, fincampanaapp, oob
          }
        ) {
          // View configuration buttons
          $data.view.elem.button.submit.class.current = $data.view.elem.button.submit.class.update;
          $data.view.elem.button.submit.title.current = $data.view.elem.button.submit.title.update;

          // Set options to combos if not are active
          if (se) $data.loadListSeM(se);
          setSelectsInactiveData(vendor, $data.view.elem.select.vendor.completeList, $data.view.elem.select.vendor.filteredList);

          $data.campana = new Campana({
            clave
            , descripcion
            , descripcionapp
            , se: se ? Number(se) : null
            , vendor: vendor ? Number(vendor) : null
            , canales: canales.split(",")
            , datelimini: getDateFromLocalToUTC(datelimini)
            , datelimend: getDateFromLocalToUTC(datelimend)
            , dateresini: getDateFromLocalToUTC(dateresini)
            , dateresend: getDateFromLocalToUTC(dateresend)
            , cuotasimple
            , benefpref
            , contactpref
            , status
            , fincampanaapp: getDateFromLocalToUTC(fincampanaapp)
            , oob
          });

          this.resetValidators();
          scrollToForm($data);
        }
        , async submit(
          { $data, $setState },
          {
            clave, descripcion, descripcionapp, se, vendor, canales, datelimini, datelimend, dateresini, dateresend, cuotasimple, benefpref, contactpref, status, fincampanaapp, oob
          }
        ) {
          try {
            if (!$data.isAllValid($data.$v)) {
              EventBus.$emit(events.SHOW_TOASTER_MESSAGE, "error", "VL0000", $data.$t("errors.validof"));
              scrollToForm($data);
              return false;
            }

            $data.view.action.submit.busy = true;

            if (oob) {
              descripcion = descripcion.toLowerCase().includes("oob") ? descripcion : "OOB_" + descripcion;
            } else {
              descripcion = descripcion.replace("OOB_", "");
            }

            const response = await campanaAPI.update({
              clave
              , descripcion
              , descripcionapp
              , se: se ? Number(se) : null
              , vendor: vendor ? Number(vendor) : null
              , canales: canales.join()
              , datelimini
              , datelimend
              , dateresini
              , dateresend
              , cuotasimple
              , benefpref
              , contactpref
              , status
              , fincampanaapp
              , oob
            });

            $data.$refs.campanaTable.refreshData();
            $data.view.action.submit.busy = false;
            $setState("initial");
            return response;
          } catch (error) {
            console.error(error);
            $data.view.action.submit.busy = false;
            return error;
          }
        }
      }
    };
  }
}



// WEBPACK FOOTER //
// ./src/views/operativos/campana/campana-state.js